import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift } from "lucide-react";
import { toast } from "sonner";

const labels = { sending: "Sending invitation", invited: "Invited", registered: "Signed up — awaiting purchase", earned: "Free evaluation earned", redeeming: "Finish claiming evaluation", redeemed: "Evaluation added" };

export default function ReferralInvites({ onRedeemed }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [papers, setPapers] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const response = await api.get("/referrals");
      setData(response.data);
      setError("");
      if (response.data.invites.some((row) => ["earned", "redeeming"].includes(row.status))) {
        const store = await api.get("/paper-store");
        setPapers(store.data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail || "Could not load referrals. Please retry."));
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/referrals", { email: email.trim() });
      setEmail("");
      toast.success("Invitation sent");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail || "Could not send invitation"));
    } finally { setBusy(false); }
  };

  const redeem = async (row) => {
    setBusy(true);
    try {
      await api.post(`/referrals/${row.id}/redeem`, { paper_id: row.paper_id || selected[row.id] });
      toast.success("Free evaluation added to your dashboard");
      await load();
      onRedeemed?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail || "Could not claim evaluation. Please retry."));
      await load();
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 bg-background border border-border rounded-lg p-6" aria-labelledby="referral-title">
      <h2 id="referral-title" className="font-heading font-bold text-lg flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /> Invite friends, earn free evaluations</h2>
      <p className="text-sm text-muted-foreground mt-2">Invite a new user by email. When they sign up with that email and make their first paper purchase, you earn one free question paper evaluation. Choose a paper below to use your reward.</p>
      <form onSubmit={invite} className="mt-4 flex flex-col sm:flex-row gap-3">
        <label className="sr-only" htmlFor="referral-email">Friend's email address</label>
        <Input id="referral-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Friend's email address" className="sm:max-w-sm" />
        <Button type="submit" disabled={busy}>{busy ? "Please wait…" : "Send invite"}</Button>
      </form>
      {error && <p role="alert" className="text-sm text-destructive mt-3">{error}</p>}
      <div className="flex items-center justify-between mt-4 gap-3">
        <p className="text-sm font-medium">{data ? `${data.available_rewards} free evaluation${data.available_rewards === 1 ? "" : "s"} available` : "Loading rewards…"}</p>
        <Button variant="ghost" size="sm" disabled={busy} onClick={load}>Refresh</Button>
      </div>
      <ul className="mt-2 space-y-3">
        {data?.invites.map((row) => (
          <li key={row.id} className="border border-border rounded-md p-3">
            <div className="flex flex-wrap justify-between gap-2 text-sm"><span className="break-all">{row.email}</span><span className="text-muted-foreground">{labels[row.status] || row.status}</span></div>
            {["earned", "redeeming"].includes(row.status) && <div className="mt-3 flex flex-col sm:flex-row gap-2">
              {row.status === "earned" && <select aria-label={`Choose free paper for invite to ${row.email}`} className="min-w-0 flex-1 rounded-md border border-input bg-background p-2 text-sm" value={selected[row.id] || ""} onChange={(e) => setSelected({ ...selected, [row.id]: e.target.value })}>
                <option value="">Choose a question paper</option>
                {papers.map((paper) => <option key={paper.id} value={paper.id}>{`${paper.title} — ${paper.subject}`}</option>)}
              </select>}
              <Button size="sm" disabled={busy || !(row.paper_id || selected[row.id])} onClick={() => redeem(row)}>{row.status === "redeeming" ? "Retry claim" : "Claim free evaluation"}</Button>
              {row.status === "earned" && papers.length === 0 && <p className="text-sm text-muted-foreground">No matching papers are available yet. Your reward is saved.</p>}
            </div>}
          </li>
        ))}
      </ul>
    </section>
  );
}
